import { DiagnosticsTypes } from '@bingads-webui-campaign-react/summary-diagnostic';
import { STEP_PATHS } from '../../step-constants';

export const Areas = {
  AssetGroup: 'AssetGroup',
  Bidding: 'Bidding',
  Budget: 'Budget',
  Campaign: 'Campaign',
  Conversion: 'Conversion',
};

export const ActionTypes = {
  View: 'View',
  Apply: 'Apply',
  SetUpNow: 'SetUpNow',
};

const AuditPoints = {
  IncreaseYourBudget: 'increase-your-budget',
  EnableAutoGeneratedAssets: 'enable-auto-generated-assets',
  NoAccountConversionGoals: 'no-account-conversion-goals',
  NoCampaignConversionGoals: 'no-campaign-conversion-goals',
  TargetCpaHigherThanBudget: 'target-cpa-higher-than-budget',
  HasNoValidGoalAtCampaignOrAccountLevel: 'has-no-valid-goal-at-campaign-or-account-level',
};

const getDefaultConversionsWarning = locator => ({
  title: _TL_('Conversion goal issues'),
  description: _TL_('There are issues with the configuration of your conversion goals. Please review the warnings to make sure they are set up correctly.'),
  type: DiagnosticsTypes.Issue,
  actions: [
    {
      type: ActionTypes.SetUpNow,
      text: _TL_('Set up now'),
      stepId: STEP_PATHS.CAMPAIGNDETAILS,
    },
  ],
  locator,
});

const getNoConversionGoalsWarning = locator => ({
  title: _TL_('No conversion goals'),
  description: _TL_('Maximize clicks will remain the default bid strategy for this campaign until you\'ve either created a conversion goal or selected a campaign level conversion goal.'),
  type: DiagnosticsTypes.Issue,
  actions: [
    {
      type: ActionTypes.SetUpNow,
      text: _TL_('Set up now'),
      stepId: STEP_PATHS.CAMPAIGNDETAILS,
    },
  ],
  locator,
});

export const ScenarioMap = {
  [Areas.Budget]: {
    LowerThanSuggestion: {
      EstimatedIncreaseInBudget: {
        title: _TL_('Increase your campaign budget'),
        description: _TL_('Your campaign budget is lower than the suggested amount, which may prevent it from performing optimally.'),
        link: {
          text: _TL_('Learn more about increasing your campaign budget.'),
          url: 'https://help.ads.microsoft.com/#apex/ads/en/53050/1',
        },
        type: DiagnosticsTypes.Issue,
        actions: [
          {
            type: ActionTypes.View,
            text: _TL_('View'),
            stepId: STEP_PATHS.BUDGET,
          },
        ],
        locator: AuditPoints.IncreaseYourBudget,
      },
    },
    InvalidCpa: {
      TargetCpaHigherThanBudget: {
        title: _TL_('Adjust your target CPA'),
        description: _TL_('Your campaign\'s total cost per acquisition is higher than the daily budget, which could prevent you from receiving any conversions. Please consider lowering your total cost per acquisition or raising your daily budget.'),
        type: DiagnosticsTypes.Issue,
        actions: [
          {
            type: ActionTypes.View,
            text: _TL_('View'),
            stepId: STEP_PATHS.BUDGET,
          },
        ],
        locator: AuditPoints.TargetCpaHigherThanBudget,
      },
    },
  },
  [Areas.Campaign]: {
    NoAutoGeneratedAssets: {
      EnableAutoGeneratedAssets: {
        title: _TL_('Enable auto-generated assets'),
        description: _TL_('Create additional AI-generated assets for your ads.'),
        type: DiagnosticsTypes.Recommendation,
        actions: [
          {
            type: ActionTypes.View,
            text: _TL_('View'),
            stepId: STEP_PATHS.CAMPAIGNDETAILS,
          },
        ],
        locator: AuditPoints.EnableAutoGeneratedAssets,
      },
    },
  },
  [Areas.Conversion]: {
    NotSetUpGoal: {
      HasNoValidCampaignLevelGoal: {
        title: _TL_('Select a conversion goal for this campaign'),
        description: _TL_('Your campaign doesn\'t have a conversion goal set up to attribute conversions, which may impact its performance.'),
        link: {
          text: _TL_('Learn more about conversion goals'),
          url: 'https://help.ads.microsoft.com/#apex/ads/en/56709/2',
        },
        type: DiagnosticsTypes.Issue,
        actions: [
          {
            type: ActionTypes.View,
            text: _TL_('View'),
            stepId: STEP_PATHS.CAMPAIGNDETAILS,
          },
        ],
        locator: AuditPoints.NoCampaignConversionGoals,
      },
      HasNoValidAccountLevelGoal: {
        title: _TL_('Create a conversion goal'),
        description: _TL_('You haven\'t created a conversion goal for this account to attribute conversions, which may impact its performance.'),
        link: {
          text: _TL_('Learn more about conversion goals'),
          url: 'https://help.ads.microsoft.com/#apex/ads/en/56709/2',
        },
        type: DiagnosticsTypes.Issue,
        locator: AuditPoints.NoAccountConversionGoals,
      },
    },
    NoConversionGoal: {
      HasNoValidGoalAtCampaignOrAccountLevel: getNoConversionGoalsWarning(AuditPoints.HasNoValidGoalAtCampaignOrAccountLevel),
      HasNoValidGoalAtCampaignOrAccountLevel_NoAccountGoal: getNoConversionGoalsWarning('has-no-valid-goal-at-campaign-or-account-level-no-account-goal'),
      HasNoValidGoalAtCampaignOrAccountLevel_ChangeToMainGoal: getDefaultConversionsWarning('has-no-valid-goal-at-campaign-or-account-level-change-to-main-goal'),
    },
    NoRecentConversion: {
      TrackingStatusNoConversions_NotUploadOfflineConversion: getDefaultConversionsWarning('tracking-status-no-conversions-not-upload-offline-conversion'),
      TrackingStatusNoConversions_EventGoalInvalidParameter: getDefaultConversionsWarning('tracking-status-no-conversions-event-goal-invalid-parameter'),
      TrackingStatusNoConversions_UrlGoalInvalidParameter: getDefaultConversionsWarning('tracking-status-no-conversions-url-goal-invalid-parameter'),
      TrackingStatusNoConversions_UETMismatch: getDefaultConversionsWarning('tracking-status-no-conversions-uet-mismatch'),
      TrackingStatusNoConversions_UETActiveMissingConversionEvent: getDefaultConversionsWarning('tracking-status-no-conversions-uet-active-missing-conversion-event'),
      TrackingStatusNoConversions_ChangeToMainGoal: getDefaultConversionsWarning('tracking-status-no-conversions-change-to-main-goal'),
      TrackingStatusNoConversions: getDefaultConversionsWarning('tracking-status-no-conversions'),
    },
    Unverified: {
      TrackingStatusUnverified_UETMismatch: getDefaultConversionsWarning('tracking-status-unverified-uet-mismatch'),
      TrackingStatusUnverified_ChangeToMainGoal: getDefaultConversionsWarning('tracking-status-unverified-change-to-main-goal'),
      TrackingStatusUnverified: getDefaultConversionsWarning('tracking-status-unverified'),
    },
    TagInactive: {
      TrackingStatusTagInactive_UETMismatch: getDefaultConversionsWarning('tracking-status-tag-inactive-uet-mismatch'),
      TrackingStatusTagInactive_ChangeToMainGoal: getDefaultConversionsWarning('tracking-status-tag-inactive-change-to-main-goal'),
      TrackingStatusTagInactive: getDefaultConversionsWarning('tracking-status-tag-inactive'),
    },
    InactiveDueToTagUnavailable: {
      TrackingStatusInactiveDueToTagUnavailable_UETMismatch: getDefaultConversionsWarning('tracking-status-inactive-due-to-tag-unavailable-uet-mismatch'),
      TrackingStatusInactiveDueToTagUnavailable_ChangeToMainGoal: getDefaultConversionsWarning('tracking-status-inactive-due-to-tag-unavailable-change-to-main-goal'),
      TrackingStatusInactiveDueToTagUnavailable: getDefaultConversionsWarning('tracking-status-inactive-due-to-tag-unavailable'),
    },
    MissGoalWithRevenueValue: {
      MissGoalWithRevenueValue: getDefaultConversionsWarning('miss-goal-with-revenue-value'),
    },
  },
};
